import { useMatchSmallViewport } from '@avensia/excite';
import { styled } from '@glitz/react';
import * as React from 'react';
import { useViewEventTrigger } from 'webapp/tracking';
import { addViewItemEventToDataLayer, ListType } from 'webapp/tracking/events/items';
import {
  createMinHugeViewportDecorator,
  createMinLargeViewportDecorator,
  createMinMediumViewportDecorator,
  createMinXLargeViewportDecorator,
} from 'webapp/viewport';
import { renderAds, renderAdsLargeViewport, renderAdsSmallViewport } from 'webapp/components/ad/renderAd';
import { LoadingProductCard, ProductCard } from 'webapp/components/ProductCard';
import { bareListDecorator } from 'webapp/decorators/list';
import { renderRetailMediaBanner } from 'webapp/components/retail-media/renderRetailMediaAd';

type PropType = {
  id: string;
  name: string;
  products: NordicNest.Api.PartialProductApiModel[];
  gridVariant?: string;
  pagination?: Excite.ContentApi.PaginationApiModel;
  defaultPaginationSize?: number;
};

const Card = ProductCard;
const LoadingCard = LoadingProductCard;

export const ProductListWithAds = React.memo(
  (
    props: PropType & {
      ads?: NordicNest.Api.AdApiModel[];
      retailMediaBannerAd?: NordicNest.Api.RetailMediaBanner;
      retailMediaSponsoredProducts?: NordicNest.Api.PartialProductApiModel[];
    },
  ) => {
    const isSmallViewport = useMatchSmallViewport();

    const productsForViewEvent = selectProductsForCurrentPage(props.products, props.defaultPaginationSize);
    const self = React.createRef<HTMLUListElement>();
    useViewEventTrigger(
      self,
      addViewItemEventToDataLayer,
      {
        products: productsForViewEvent,
        list_id: props.id,
        list_name: props.name,
        list_type: ListType.ProductListingPage,
        list_pagination_index: props.pagination?.currentPage,
        list_pagination_max: props.pagination?.pageCount,
        defaultPaginationSize: props.defaultPaginationSize,
      },
      productsForViewEvent.map(x => x.sku).join(','),
    );

    const nrLoadedProducts = props.products.length;
    const hasAds = props.ads && props.ads.length > 0;

    function calculateProductCardPage(productIndex: number): number {
      return baseCalculateProductCardPage(
        props.pagination?.currentPage,
        nrLoadedProducts,
        productIndex,
        props.defaultPaginationSize,
      );
    }

    function calculateProductCardIndex(productIndex: number): number {
      return baseCalculateProductCardIndex(
        calculateProductCardPage(productIndex),
        productIndex,
        props.defaultPaginationSize,
      );
    }

    const productList =
      props.pagination?.currentPage === 1 && props.retailMediaSponsoredProducts
        ? injectSponsoredProductsIntoProductList(props.products, props.retailMediaSponsoredProducts)
        : props.products;

    return (
      <Base ref={self} css={gridDecorator(props.gridVariant)}>
        {productList?.map((product, index) => (
          <React.Fragment key={product.url + index}>
            {props.gridVariant !== undefined
              ? renderAds(index, props.ads, isSmallViewport, props.gridVariant)
              : isSmallViewport
              ? renderAdsSmallViewport(index, props.ads)
              : renderAdsLargeViewport(index, props.ads)}
            {renderRetailMediaBanner(index, props.retailMediaBannerAd, isSmallViewport, hasAds)}
            <Item css={props.gridVariant !== undefined && { order: index + 1 }}>
              <Card
                list_id={props.id}
                list_name={props.name}
                list_type={ListType.ProductListingPage}
                index={calculateProductCardIndex(index)}
                list_pagination_max={props.pagination?.pageCount}
                list_pagination_index={calculateProductCardPage(index)}
                product={product}
              />
            </Item>
          </React.Fragment>
        ))}
      </Base>
    );
  },
);

export default React.memo(function ProductList(props: PropType) {
  const self = React.createRef<HTMLUListElement>();
  const productsForViewEvent = selectProductsForCurrentPage(props.products, props.defaultPaginationSize);
  useViewEventTrigger(
    self,
    addViewItemEventToDataLayer,
    {
      products: productsForViewEvent,
      list_id: props.id,
      list_name: props.name,
      list_type: ListType.ProductListingPage,
      list_pagination_index: props.pagination?.currentPage,
      list_pagination_max: props.pagination?.pageCount,
    },
    productsForViewEvent.map(x => x.sku).join(','),
  );

  const nrLoadedProducts = props.products.length;

  function calculateProductCardPage(productIndex: number): number {
    return baseCalculateProductCardPage(
      props.pagination?.currentPage,
      nrLoadedProducts,
      productIndex,
      props.defaultPaginationSize,
    );
  }

  function calculateProductCardIndex(productIndex: number): number {
    return baseCalculateProductCardIndex(
      calculateProductCardPage(productIndex),
      productIndex,
      props.defaultPaginationSize,
    );
  }

  return (
    <Base ref={self} css={gridDecorator(props.gridVariant)}>
      {props.products?.map((product, index) => (
        <Item key={product.url + index}>
          <Card
            list_id={props.id}
            list_name={props.name}
            list_type={ListType.ProductListingPage}
            index={calculateProductCardIndex(index)}
            product={product}
            list_pagination_max={props.pagination?.pageCount}
            list_pagination_index={calculateProductCardPage(index)}
          />
        </Item>
      ))}
    </Base>
  );
});

function selectProductsForCurrentPage(
  allListedProducts: NordicNest.Api.PartialProductApiModel[],
  defaultPaginationSize: number | undefined,
): NordicNest.Api.PartialProductApiModel[] {
  if (!defaultPaginationSize) return allListedProducts;

  const nrLoadedPages = Math.floor(allListedProducts.length / defaultPaginationSize);
  let skip = (nrLoadedPages - 1) * defaultPaginationSize;
  skip = skip > 0 ? skip : 0;

  return allListedProducts.slice(skip, skip + defaultPaginationSize);
}

function baseCalculateProductCardPage(
  currentPage: number | undefined,
  nrLoadedProducts: number,
  productIndex: number,
  defaultPaginationSize: number | undefined,
): number {
  if (!currentPage || !defaultPaginationSize) return 0;

  const pageAtProductIndexZero = currentPage - Math.floor((nrLoadedProducts - 1) / defaultPaginationSize);
  const productPageStartingFromLoadedProducts = Math.floor(productIndex / defaultPaginationSize);
  return pageAtProductIndexZero + productPageStartingFromLoadedProducts;
}

function baseCalculateProductCardIndex(
  productCardPage: number,
  productIndex: number,
  defaultPaginationSize: number | undefined,
): number {
  if (!defaultPaginationSize) return productIndex;

  return (productCardPage - 1) * defaultPaginationSize + ((productIndex % defaultPaginationSize) + 1);
}

export function LoadingProductList(props: { count: number }) {
  return (
    <Base>
      {new Array(props.count).fill(1).map(({}, i) => (
        <Item key={i}>
          <LoadingCard />
        </Item>
      ))}
    </Base>
  );
}

function injectSponsoredProductsIntoProductList(
  products: NordicNest.Api.PartialProductApiModel[],
  sponsoredProducts: NordicNest.Api.PartialProductApiModel[] | undefined,
) {
  // Remove any duplicates already in the product results
  const sponsoredSkus = new Set(sponsoredProducts?.map(p => p.sku) || []);
  const productList = products.filter(
    product =>
      product?.retailMediaSponsoredProductContext?.isSponsoredProduct !== true && !sponsoredSkus.has(product.sku),
  );

  for (const sponsoredProduct of sponsoredProducts || []) {
    productList.splice(sponsoredProduct.retailMediaSponsoredProductContext.position - 1, 0, sponsoredProduct);
  }

  return productList;
}

/** @glitz-dynamic */
const Base = styled.ul(
  bareListDecorator,
  createMinLargeViewportDecorator({
    gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))',
  }),
  createMinMediumViewportDecorator({
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
  }),
  {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
    gridColumnGap: t => t.sharedSpacing.xs,
    gridRowGap: t => t.sharedSpacing.xs,
  },
);

const gridDecorator = (gridVariant?: string) => {
  switch (gridVariant) {
    case 'max1920px':
      return styled(
        createMinXLargeViewportDecorator({
          gridTemplateColumns: 'repeat(5, 1fr)',
        }),
      );
    case 'max3440px':
      return styled(
        createMinHugeViewportDecorator({
          gridTemplateColumns: 'repeat(6, 1fr)',
        }),
        createMinXLargeViewportDecorator({
          gridTemplateColumns: 'repeat(5, 1fr)',
        }),
      );
    default:
      return {};
  }
};

const Item = styled.li();
